import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { ApolloLink } from "apollo-link";
import { onError } from "apollo-link-error";
import { createUploadLink } from "apollo-upload-client";
import gql from "graphql-tag";
import Cookies from "js-cookie";
import React, { ReactNode, Suspense } from "react";
import { ApolloProvider, Query, QueryResult } from "react-apollo";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";
import { AdminLayout } from "./components/AdminLayout";
import { Loader } from "./components/Loader";
import { UserContext } from "./context/UserContext";
import { HelperWebsite } from "./function/websites";
import { Graph } from "./generated/graph";
import routes from "./routes";
import { Error404Screen } from "./screens/Maintenance/Error404Screen";
import { Error500Screen } from "./screens/Maintenance/Error500Screen";
import "./scss/main.scss";

const VERIFY_QUERY = gql`
  query VerifyToken {
    me {
      id
      username
      name {
        en
      }
      groupId
      role
      profilePicture
      uploadStorage
      superPermission
      permissions
      totalBalanceAfterTax
    }

    configuration {
      websites {
        siteId
        siteName
        siteColor
      }
    }
  }
`;

type route = {
  path: string;
  title: string;
  notRedirect?: boolean;
  component: ReactNode;
};

type StateType = {
  token: string;
  apolloClient: ApolloClient<any>;
};

class App extends React.Component<any> {
  state: StateType;
  isAuthenticated: boolean = false;

  constructor(props: any) {
    super(props);

    let token = Cookies.get("token");
    token = token ? token : "";

    this.state = {
      token: token,
      apolloClient: this.createApolloClient(token),
    };
  }

  render() {
    return (
      <div className="App">
        <ApolloProvider client={this.state.apolloClient}>
          <Query query={VERIFY_QUERY}>{this.renderClient}</Query>
        </ApolloProvider>
      </div>
    );
  }

  renderClient = ({ data, loading, error }: QueryResult<Graph.Query>) => {
    if (loading) return <Loader />;
    if (error || data === undefined) return <Error500Screen />;

    if (data.configuration) {
      HelperWebsite.websites = data.configuration.websites!;
    }

    if (data.me !== undefined && data.me !== null) {
      this.isAuthenticated = true;
      const user: Graph.User = data.me;

      //Resubscribe notification every last login 24hours
      // const lastSubscribe = Cookies.get('_notiSubDate');
      // if(!lastSubscribe) {
      //   const token = Cookies.get('token');
      //   const notificationUrl = (window as any).setting.notificationUrl + '&token=' + token;

      //   window.location.href = notificationUrl;
      //   Cookies.set("_notiSubDate", new Date(), { expires: 5 })
      // }

      return (
        <UserContext.Provider value={{ user }}>
          {this.renderRoutes(user)}
        </UserContext.Provider>
      );
    }

    return this.renderRoutes(null);
  };

  setClient = (token) => {
    this.setState({
      token,
      apolloClient: this.createApolloClient(token),
    });
    Cookies.set("token", token, { expires: 365 });
  };

  renderRoutes = (user: Graph.User | null) => {
    const publicRoutes = routes.public.map((route, inx) =>
      this.renderPublicRoute(route, inx)
    );
    const privateRoutes = routes.private.map((route, inx) =>
      this.renderPrivateRoute(route, inx, user)
    );

    return (
      <BrowserRouter>
        <Suspense fallback={<Loader />}>
          <Switch>
            {publicRoutes}
            {privateRoutes}

            {/* when none of the above match, <PageNotFoundScreen> will be rendered */}
            <Route component={Error404Screen} />
          </Switch>
        </Suspense>
      </BrowserRouter>
    );
  };

  renderPublicRoute = (route: route, inx: number) => {
    return (
      <PublicRoute
        key={inx}
        isAuthenticated={this.isAuthenticated}
        exact
        path={route.path}
        title={route.title}
        notRedirect={route.notRedirect}
        comp={route.component}
      />
    );
  };

  renderPrivateRoute = (route: route, inx: number, user: Graph.User | null) => {
    return (
      <PrivateRoute
        key={inx}
        isAuthenticated={this.isAuthenticated}
        exact
        path={route.path}
        title={route.title}
        comp={route.component}
        user={user}
      />
    );
  };

  createApolloClient = (token: string | null) => {
    let uri: string = (window as any).setting.endpoint as string;
    console.log(`Pointing end point: ${uri}`);

    if (token !== undefined && token !== null && token.length > 5) {
      uri += `?token=${token}`;
    }

    const uploadLink = createUploadLink({
      uri: uri,
    });

    const client = new ApolloClient({
      link: ApolloLink.from([
        onError(({ graphQLErrors, networkError }) => {
          if (graphQLErrors)
            graphQLErrors.map(({ message, locations, path }) =>
              console.log(
                `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
              )
            );
          if (networkError) console.log(`[Network error]: ${networkError}`);
        }),
        uploadLink,
      ]),
      cache: new InMemoryCache(),
      name: "editor",
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
    });

    return client;
  };
}

function PublicRoute({ ...props }: any) {
  return (
    <Route
      {...props}
      render={({ ...routeProps }) =>
        !props.isAuthenticated || props.notRedirect ? (
          <props.comp {...routeProps} />
        ) : (
          <Redirect to={"/"} />
        )
      }
    />
  );
}

function PrivateRoute({ user, ...props }: any) {
  const redirect_url =
    props.location.pathname === "/"
      ? ""
      : "?redirect=" + props.location.pathname;

  return (
    <Route
      {...props}
      render={({ ...routeProps }) =>
        props.isAuthenticated ? (
          <AdminLayout user={user}>
            <props.comp {...routeProps} />
          </AdminLayout>
        ) : (
          <Redirect to={"/signin" + redirect_url} />
        )
      }
    />
  );
}

export default App;
